import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar, TAB_TITLES, type ConsoleTab } from "@/components/console/Sidebar";
import { TopBar } from "@/components/console/TopBar";
import { Panels } from "@/components/console/Panels";
import { fetchApiKey, fetchMe, login, logout as apiLogout } from "@/lib/api";
import {
  clearAccountCache,
  clearLegacyAPIKey,
  clearSession,
  readLegacyAPIKey,
  readStoredSession,
  saveSession,
} from "@/lib/storage";
import type { ApiKey, UserPublic } from "@/lib/types";

const VALID_TABS: ConsoleTab[] = ["market", "usage", "account", "recharge"];

// 旧 hash 兼容：#config / #install 都映射到 #market（应用市场）
const LEGACY_TAB_ALIASES: Record<string, ConsoleTab> = {
  config: "market",
  install: "market",
};

function readHashTab(): ConsoleTab {
  const h = window.location.hash.replace(/^#/, "");
  if ((VALID_TABS as string[]).includes(h)) return h as ConsoleTab;
  if (h in LEGACY_TAB_ALIASES) return LEGACY_TAB_ALIASES[h];
  return "market";
}

export default function ConsolePage() {
  const navigate = useNavigate();
  const [session, setSession] = useState<string>(() => readStoredSession());
  const [user, setUser] = useState<UserPublic | null>(null);
  const [apiKey, setApiKey] = useState<ApiKey | null>(null);
  const [tab, setTab] = useState<ConsoleTab>(readHashTab);
  const [loading, setLoading] = useState<boolean>(true);
  const meFetchedRef = useRef(false);

  // tab ↔ url hash sync
  useEffect(() => {
    const onHash = () => setTab(readHashTab());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Bootstrap: convert legacy api-key cookie to session if needed,
  // then fetch /me + /me/api_key.
  useEffect(() => {
    if (meFetchedRef.current) return;
    meFetchedRef.current = true;

    const bootstrap = async () => {
      let token = session;
      if (!token) {
        const legacy = readLegacyAPIKey();
        if (legacy) {
          try {
            const resp = await login({ method: "api_key", api_key: legacy });
            token = resp.session_token;
            saveSession(token, true);
            clearLegacyAPIKey();
            setSession(token);
            setUser(resp.user);
          } catch {
            clearLegacyAPIKey();
          }
        }
      }
      if (!token) {
        navigate("/#console", { replace: true });
        return;
      }
      try {
        const [me, key] = await Promise.all([
          fetchMe(token),
          fetchApiKey(token).catch(() => null),
        ]);
        setUser(me.user);
        if (key) setApiKey(key.api_key);
        setLoading(false);
      } catch {
        clearSession();
        clearAccountCache();
        setSession("");
        setUser(null);
        navigate("/#console", { replace: true });
      }
    };
    void bootstrap();
  }, [session, navigate]);

  const setTabAndHash = useCallback((next: ConsoleTab) => {
    setTab(next);
    if (window.location.hash !== `#${next}`) {
      window.location.hash = next;
    }
  }, []);

  const handleLogout = useCallback(() => {
    if (session) void apiLogout(session).catch(() => {});
    clearSession();
    clearAccountCache();
    setSession("");
    setUser(null);
    setApiKey(null);
    navigate("/#console", { replace: true });
  }, [navigate, session]);

  const handleApiKeyUpdate = useCallback((next: ApiKey | null) => {
    setApiKey(next);
  }, []);

  const handleUserUpdate = useCallback((next: UserPublic) => {
    setUser(next);
  }, []);

  const [title, subtitle] = useMemo(() => TAB_TITLES[tab], [tab]);

  if (loading || !session || !user) {
    return (
      <main className="c2-shell">
        <div className="c2-main">
          <header className="c2-topbar">
            <div className="c2-topbar-title">
              <h1>正在加载…</h1>
              <p>正在校验登录状态。</p>
            </div>
          </header>
        </div>
      </main>
    );
  }

  return (
    <main className="c2-shell">
      <Sidebar active={tab} onChange={setTabAndHash} />
      <div className="c2-main">
        <TopBar
          title={title}
          subtitle={subtitle}
          user={user}
          apiKey={apiKey}
          onLogout={handleLogout}
        />
        <Panels
          active={tab}
          session={session}
          user={user}
          apiKey={apiKey}
          onApiKeyUpdate={handleApiKeyUpdate}
          onUserUpdate={handleUserUpdate}
          onTabChange={setTabAndHash}
        />
      </div>
    </main>
  );
}
